// Campi del worklog condivisi da form di inserimento e finestra di modifica.
// pill = stile delle pagine V2 (niente etichetta sul bordo, icona, verde quando compilato), usato nel form di inserimento.
import React from 'react';
import { Autocomplete, Box, InputAdornment, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { Check as CheckIcon, FolderOutlined as ProjectIcon, LocalOfferOutlined as ActivityIcon } from '@mui/icons-material';
import { formatMinutes, parseEffort } from '../../utils/effortParser';
import { MAX_DESCRIPTION_LENGTH } from '../../utils/worklogRules';
import { GREEN, GREEN_DARK, GREEN_SOFT, INK, LINE, MUTED } from '../home/homeTheme';

// Menu delle scelte nello stesso stile dei pannelli dei filtri V2: angoli arrotondati, righe ariose, scelta in verde
const menuProps = {
  slotProps: {
    paper: {
      sx: {
        mt: 0.75,
        borderRadius: 2,
        border: `1px solid ${LINE}`,
        boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
        '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': { fontSize: 13, color: MUTED },
      },
    },
  },
  ListboxProps: {
    sx: {
      py: 0.5,
      maxHeight: 320,
      '& .MuiAutocomplete-option': { mx: 0.5, px: 1.25, py: 0.75, minHeight: 0, gap: 1, borderRadius: 1.5 },
      '& .MuiAutocomplete-option.Mui-focused': { bgcolor: 'var(--rt-hover)' },
      '& .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option[aria-selected="true"].Mui-focused': { bgcolor: GREEN_SOFT },
      '&::-webkit-scrollbar': { width: 8 },
      '&::-webkit-scrollbar-thumb': { bgcolor: 'var(--rt-pill-border)', borderRadius: 4 },
    },
  },
};

/** Testo con la parte cercata in grassetto. */
function Highlight({ text, query }) {
  const q = (query || '').trim();
  const idx = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="span" sx={{ fontWeight: 800, color: GREEN_DARK }}>
        {text.slice(idx, idx + q.length)}
      </Box>
      {text.slice(idx + q.length)}
    </>
  );
}

/** Riga del menu: nome (con la ricerca evidenziata), riga secondaria e spunta sulla scelta attuale. */
function MenuOption({ props, name, caption, selected, query }) {
  return (
    <Box component="li" {...props}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontSize: 13.5, color: INK, fontWeight: selected ? 700 : 500 }}>
          <Highlight text={name} query={query} />
        </Typography>
        {caption && (
          <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
            {caption}
          </Typography>
        )}
      </Box>
      {selected && <CheckIcon sx={{ fontSize: 18, color: GREEN_DARK, flexShrink: 0 }} />}
    </Box>
  );
}

// La ricerca si evidenzia solo mentre si scrive, non quando il campo mostra la scelta attuale
const typedQuery = (inputValue, currentLabel) => (inputValue && inputValue !== currentLabel ? inputValue : '');

// Helper text su una riga: il testo completo resta nel tooltip nativo
export const oneLineHelper = (text) => ({
  title: typeof text === 'string' ? text : undefined,
  sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mx: 0.5 },
});

/** Stile "a pillola" di un campo: bordo chiaro, verde quando compilato. */
export const pillFieldSx = (filled) => ({
  '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: filled ? GREEN_SOFT : 'var(--rt-paper)', fontSize: 13.5, fontWeight: filled ? 600 : 400, color: INK },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: filled ? GREEN : 'var(--rt-pill-border)' },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: filled ? GREEN_DARK : 'var(--rt-pill-border-hover)' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN, borderWidth: 1 },
  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f' },
  '& .MuiInputBase-input::placeholder': { color: MUTED, opacity: 1, fontWeight: 400 },
});
const pillIcon = (Icon, filled) => (
  <InputAdornment position="start" sx={{ mr: 0.25 }}>
    <Icon sx={{ fontSize: 18, color: filled ? GREEN_DARK : MUTED }} />
  </InputAdornment>
);
// In stile pillola l'helper compare solo con un errore; nei form classici resta lo spazio riservato
const helperProps = (pill, text) => (pill ? { helperText: text || undefined } : { helperText: text || ' ' });

const projectLabel = (p) => (p ? `${p.name}${p.customer?.name ? ` · ${p.customer.name}` : ''}` : '');

export function ProjectField({ projects, value, onChange, error, disabled, inputRef, pill }) {
  const project = projects.find((p) => p.id === value) || null;
  return (
    <Autocomplete
      size="small"
      options={projects}
      value={project}
      disabled={disabled}
      getOptionLabel={projectLabel}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_, p) => onChange(p ? p.id : '')}
      noOptionsText="No matching projects"
      {...menuProps}
      renderOption={(props, p, { selected, inputValue }) => (
        <MenuOption
          key={p.id}
          props={props}
          name={p.name}
          caption={p.customer?.name}
          selected={selected}
          query={typedQuery(inputValue, projectLabel(project))}
        />
      )}
      sx={pill ? pillFieldSx(!!project) : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          label={pill ? undefined : 'Project'}
          placeholder={pill ? 'Project' : undefined}
          inputRef={inputRef}
          error={!!error}
          {...helperProps(pill, error)}
          FormHelperTextProps={oneLineHelper(error)}
          InputProps={pill ? { ...params.InputProps, startAdornment: pillIcon(ProjectIcon, !!project) } : params.InputProps}
        />
      )}
    />
  );
}

export function ActivityField({ activities, value, onChange, error, disabled, pill }) {
  const activity = activities.find((a) => a.id === value) || null;
  return (
    <Autocomplete
      size="small"
      options={activities}
      value={activity}
      disabled={disabled}
      getOptionLabel={(a) => a?.name || ''}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_, a) => onChange(a ? a.id : '')}
      noOptionsText="No matching activities"
      {...menuProps}
      renderOption={(props, a, { selected, inputValue }) => (
        <MenuOption
          key={a.id}
          props={props}
          name={a.name}
          caption={[a.requires_ticket_id && 'Ticket required', a.requires_description && 'Description required'].filter(Boolean).join(' · ')}
          selected={selected}
          query={typedQuery(inputValue, activity?.name)}
        />
      )}
      sx={pill ? pillFieldSx(!!activity) : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          label={pill ? undefined : 'Activity'}
          placeholder={pill ? 'Activity' : undefined}
          error={!!error}
          {...helperProps(pill, error)}
          FormHelperTextProps={oneLineHelper(error)}
          InputProps={pill ? { ...params.InputProps, startAdornment: pillIcon(ActivityIcon, !!activity) } : params.InputProps}
        />
      )}
    />
  );
}

export function EffortTypeField({ effortTypes, value, onChange, error, disabled, pill }) {
  const known = effortTypes.some((et) => et.id === value);
  return (
    <TextField
      select
      size="small"
      label={pill ? undefined : 'Effort type'}
      fullWidth
      value={known ? value : ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      error={!!error}
      {...helperProps(pill, error)}
      sx={pill ? pillFieldSx(known) : undefined}
      SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 360 } } } }}
    >
      {effortTypes.map((et) => (
        <MenuItem key={et.id} value={et.id}>
          {et.name.trim()}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** Ticket con prefisso bloccato mostrato come etichetta compatta. */
export function TicketField({ value, onChange, prefix, required, hint, error, disabled, onPaste, inputProps, onFocus, pill }) {
  const shown = prefix && value.startsWith(prefix) ? value.slice(prefix.length) : value;
  const helper = error || (pill ? '' : hint) || '';
  const label = required ? 'Ticket ID *' : 'Ticket ID';
  return (
    <TextField
      size="small"
      fullWidth
      label={pill ? undefined : label}
      placeholder={pill ? label : undefined}
      title={pill && hint ? hint : undefined}
      value={shown}
      disabled={disabled}
      onPaste={onPaste}
      onFocus={onFocus}
      onChange={(e) => {
        const typed = e.target.value;
        onChange(prefix && !typed.startsWith(prefix) ? prefix + typed : typed);
      }}
      inputProps={inputProps}
      sx={pill ? pillFieldSx(!!shown) : undefined}
      InputProps={
        prefix
          ? {
              startAdornment: (
                <InputAdornment position="start" sx={{ maxWidth: '45%', mr: 0.5 }}>
                  <Tooltip title={`Fixed prefix for this project: ${prefix}`}>
                    <Box
                      component="span"
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: pill ? 'var(--rt-prefix-bg)' : 'var(--rt-grey-100)',
                        color: 'text.secondary',
                        fontSize: 12,
                        fontFamily: 'monospace',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                      }}
                    >
                      {prefix}
                    </Box>
                  </Tooltip>
                </InputAdornment>
              ),
            }
          : undefined
      }
      error={!!error}
      {...helperProps(pill, helper)}
      FormHelperTextProps={oneLineHelper(helper.trim() ? helper : undefined)}
    />
  );
}

/** Effort solo in minuti (numero intero); sotto il campo la conversione in ore come aiuto. */
export function EffortField({ value, onChange, error, disabled, onPaste, inputRef, onFocus, pill }) {
  const parsed = parseEffort(value);
  let helper = error || '';
  if (!error && !pill && parsed.valid) helper = `= ${formatMinutes(parsed.minutes)}`;
  else if (!error && parsed.error) helper = parsed.error;
  return (
    <TextField
      size="small"
      fullWidth
      label={pill ? undefined : 'Effort (min) *'}
      placeholder={pill ? 'Minutes *' : '90'}
      title={pill && parsed.valid ? `= ${formatMinutes(parsed.minutes)}` : undefined}
      value={value}
      disabled={disabled}
      inputRef={inputRef}
      onPaste={onPaste}
      onFocus={onFocus}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
      inputProps={{ inputMode: 'numeric' }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Typography sx={{ fontSize: 12, color: MUTED }}>min</Typography>
          </InputAdornment>
        ),
      }}
      sx={pill ? pillFieldSx(parsed.valid) : undefined}
      error={!!error || (!parsed.empty && !parsed.valid)}
      {...helperProps(pill, helper)}
      FormHelperTextProps={oneLineHelper(helper || undefined)}
    />
  );
}

export function DescriptionField({ value, onChange, required, error, disabled, onPaste, onFocus, pill }) {
  const label = required ? 'Description *' : 'Description';
  return (
    <TextField
      size="small"
      fullWidth
      label={pill ? undefined : label}
      placeholder={pill ? label : undefined}
      value={value}
      disabled={disabled}
      onPaste={onPaste}
      onFocus={onFocus}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
      sx={pill ? pillFieldSx(!!value.trim()) : undefined}
      error={!!error}
      {...helperProps(pill, error)}
      FormHelperTextProps={oneLineHelper(error)}
    />
  );
}
